import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentTaxpayerId,
  setCurrentTaxpayerId,
  clearTaxContext,
} from './taxContextSlice'
import useAuth from '../../hooks/useAuth'

const APP_KEY = 'PAJAK_AUTOMATION'

const TaxpayerContextSelector = ({
  taxpayers = [],
  isLoading = false,
  className = '',
  label = 'Taxpayer',
  autoSelectFirst = true,
  showLabel = true,
  value,          // optional controlled value
  onChange,       // optional controlled onChange
}) => {
  const dispatch = useDispatch()
  const reduxTaxpayerId = useSelector(selectCurrentTaxpayerId)

  const {
    isGlobalAdmin,
    hasAppRole,
    getAppAccess,
  } = useAuth()

  const pajakAccess = getAppAccess(APP_KEY)

  const isTenantAdmin = hasAppRole(APP_KEY, 'TENANT_ADMIN')
  const isTaxPayer = hasAppRole(APP_KEY, 'TAX_PAYER')
  const isTaxpayerOnly = !isGlobalAdmin && !isTenantAdmin && isTaxPayer

  const scopedCustomerId = pajakAccess?.scope?.customer_id ?? null
  const scopedTaxpayerId = pajakAccess?.scope?.taxpayer_id ?? null

  const isControlled = value !== undefined && typeof onChange === 'function'
  const selectedValue = isControlled ? value : reduxTaxpayerId

  const setSelectedTaxpayer = (nextValue) => {
    if (isControlled) {
      onChange(nextValue)
    } else {
      dispatch(setCurrentTaxpayerId(nextValue))
    }
  }

  const normalizedTaxpayers = useMemo(() => {
    let taxpayerArray = []

    if (Array.isArray(taxpayers)) {
      taxpayerArray = taxpayers
    } else if (taxpayers?.ids && taxpayers?.entities) {
      taxpayerArray = taxpayers.ids
        .map((id) => taxpayers.entities[id])
        .filter(Boolean)
    }

    return taxpayerArray.map((tp) => ({
      taxpayer_id: Number(tp.taxpayer_id),
      customer_id: tp.customer_id != null ? Number(tp.customer_id) : null,
      full_name: tp.full_name ?? '-',
      npwp: tp.npwp ?? '',
    }))
  }, [taxpayers])

  const availableTaxpayers = useMemo(() => {
    if (isGlobalAdmin) {
      return normalizedTaxpayers
    }

    if (isTenantAdmin) {
      return normalizedTaxpayers
    }

    if (isTaxpayerOnly) {
      return normalizedTaxpayers.filter(
        (tp) => tp.taxpayer_id === Number(scopedTaxpayerId)
      )
    }

    return []
  }, [
    normalizedTaxpayers,
    isGlobalAdmin,
    isTenantAdmin,
    isTaxpayerOnly,
    scopedTaxpayerId,
  ])

  useEffect(() => {
    if (isTaxpayerOnly) {
      if (
        scopedTaxpayerId != null &&
        Number(selectedValue) !== Number(scopedTaxpayerId)
      ) {
        setSelectedTaxpayer(Number(scopedTaxpayerId))
      }
      return
    }

    if ((isGlobalAdmin || isTenantAdmin) && availableTaxpayers.length > 0) {
      const currentStillValid = availableTaxpayers.some(
        (tp) => tp.taxpayer_id === Number(selectedValue)
      )

      if (!currentStillValid) {
        if (autoSelectFirst) {
          setSelectedTaxpayer(availableTaxpayers[0].taxpayer_id)
        } else if (!isControlled) {
          dispatch(clearTaxContext())
        } else {
          onChange?.(null)
        }
      }
      return
    }

    if (availableTaxpayers.length === 0) {
      if (!isControlled) {
        dispatch(clearTaxContext())
      } else {
        onChange?.(null)
      }
    }
  }, [
    dispatch,
    isControlled,
    onChange,
    isTaxpayerOnly,
    isGlobalAdmin,
    isTenantAdmin,
    scopedTaxpayerId,
    selectedValue,
    availableTaxpayers,
    autoSelectFirst,
  ])

  const handleChange = (e) => {
    const nextValue = e.target.value ? Number(e.target.value) : null
    setSelectedTaxpayer(nextValue)
  }

  if (!isGlobalAdmin && !isTenantAdmin && !isTaxPayer) {
    return null
  }

  if (isLoading) {
    return (
      <div className={className}>
        {showLabel && (
          <label className="mb-1 block text-sm font-medium text-slate-700">
            {label}
          </label>
        )}
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500">
          Loading taxpayers...
        </div>
      </div>
    )
  }

  if (!isTaxpayerOnly && availableTaxpayers.length === 0) {
    return (
      <div className={className}>
        {showLabel && (
          <label className="mb-1 block text-sm font-medium text-slate-700">
            {label}
          </label>
        )}
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          No taxpayer data available.
        </div>
      </div>
    )
  }

  if (isTaxpayerOnly) {
    const lockedTaxpayer =
      availableTaxpayers[0] ||
      normalizedTaxpayers.find(
        (tp) => tp.taxpayer_id === Number(scopedTaxpayerId)
      )

    return (
      <div className={className}>
        {showLabel && (
          <label className="mb-1 block text-sm font-medium text-slate-700">
            {label}
          </label>
        )}
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-sm font-medium text-slate-800">
            {lockedTaxpayer?.full_name ?? 'Assigned taxpayer'}
          </div>
          {lockedTaxpayer?.npwp ? (
            <div className="text-xs text-slate-500">{lockedTaxpayer.npwp}</div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {showLabel && (
        <label
          htmlFor="taxpayer-context-selector"
          className="mb-1 block text-sm font-medium text-slate-700"
        >
          {label}
        </label>
      )}

      <select
        id="taxpayer-context-selector"
        value={selectedValue ?? ''}
        onChange={handleChange}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
      >
        {!autoSelectFirst && <option value="">Select taxpayer</option>}

        {availableTaxpayers.map((tp) => (
          <option key={tp.taxpayer_id} value={tp.taxpayer_id}>
            {tp.full_name}
            {tp.npwp ? ` - ${tp.npwp}` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

export default TaxpayerContextSelector